import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = ["Brand Integration", "Ad Film", "Other"];
const initial = { full_name: "", company: "", email: "", phone: "", service: "Brand Integration", budget: "", timeline: "", description: "", heard_from: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.description) {
      toast.error("Please fill in your name, email, and project description.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! Our team will be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 py-3 text-offwhite placeholder-white/30 focus:border-gold focus:outline-none transition-colors duration-300 font-light";

  return (
    <section id="contact" className="py-16 md:py-20 container-x scroll-mt-24" data-testid="contact-section">
      <SectionHeading eyebrow="Contact" title={"Let's Create\nSomething Memorable"} className="mb-16 max-w-4xl" />
      <div className="max-w-3xl">
          <form onSubmit={submit} data-testid="enquiry-form" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/40">Full Name *</label>
                <input data-testid="input-full-name" value={form.full_name} onChange={update("full_name")} className={inputCls} placeholder="Your name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/40">Company / Brand</label>
                <input data-testid="input-company" value={form.company} onChange={update("company")} className={inputCls} placeholder="Company or brand" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/40">Email *</label>
                <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/40">Phone</label>
                <input data-testid="input-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="+91 ..." />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/40">Service Required</label>
                <select data-testid="input-service" value={form.service} onChange={update("service")} className={`${inputCls} [&>option]:bg-surface`}>
                  {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/40">Estimated Budget</label>
                <input data-testid="input-budget" value={form.budget} onChange={update("budget")} className={inputCls} placeholder="e.g. ₹ range" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Project Description *</label>
              <textarea data-testid="input-description" value={form.description} onChange={update("description")} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your project..." />
            </div>
            <button type="submit" data-testid="submit-enquiry" disabled={loading} className="group relative inline-flex items-center justify-center gap-2 bg-gold text-void px-10 py-5 uppercase tracking-[0.2em] text-xs font-semibold overflow-hidden disabled:opacity-70">
              <span className="relative z-10 flex items-center gap-2">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <>Submit Enquiry <Send size={15} /></>}
              </span>
              <span className="absolute inset-0 bg-champagne scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </button>
          </form>
      </div>
    </section>
  );
};
